import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Container,
  Snackbar,
} from '@mui/material'
import {
  Add,
  Edit,
  Visibility,
  Groups,
  Delete as DeleteIcon,
  CheckCircle,
  Error as ErrorIcon,
  CloudUpload,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { useGetTeamsQuery, useCreateTeamMutation, useUpdateTeamMutation, useReviewTeamMutation, useUploadMemberCardMutation, useUploadTeamProfileFileMutation, useGetTeamCasesQuery, type Team, type TeamCreate, type MemberCardInfo, type MemberDetails, type CoachDetails } from '../store/api/teamsApi'
import { useLazyGetUserByEmailQuery, useGetUsersBatchQuery, useFetchUsersBatchMutation } from '../store/api/usersApi'
import { hasPermission } from '../utils/permissions'
import { gradientBrand } from '../theme/brand'
import { PageHeader } from '../components/PageHeader'
import { getTablePaperSx } from '../theme/sectionSurfaces'

const STATUS_OPTIONS = [
  { value: 'registered', label: 'Đã đăng ký' },
  { value: 'confirmed', label: 'Đã xác nhận' },
  { value: 'active', label: 'Đã duyệt (hoạt động)' },
  { value: 'completed', label: 'Đã hoàn thành' },
  { value: 'rejected', label: 'Bị từ chối' },
]

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  registered: 'info',
  confirmed: 'primary',
  active: 'success',
  completed: 'default',
  rejected: 'error',
}

interface MemberEmail {
  email: string
  userId?: string
  fullName?: string
  role?: string
  isValid?: boolean
  isLoading?: boolean
  error?: string
}

type MemberCardsMap = Record<string, MemberCardInfo>
type MemberDetailsMap = Record<string, MemberDetails>

const EMPTY_MEMBER_DETAILS: MemberDetails = {
  student_id: '',
  full_name: '',
  date_of_birth: '',
  university: '',
  faculty: '',
  email: '',
  bio: '',
}

const EMPTY_COACH_DETAILS: CoachDetails = {
  full_name: '',
  email: '',
  university: '',
  faculty: '',
  bio: '',
}

export default function TeamsPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const { data: teams = [], isLoading, error, refetch } = useGetTeamsQuery()
  const [createTeam, { isLoading: isCreating }] = useCreateTeamMutation()
  const [updateTeam, { isLoading: isUpdating }] = useUpdateTeamMutation()
  const [uploadMemberCard, { isLoading: isUploadingCard }] = useUploadMemberCardMutation()
  const [uploadTeamProfileFile, { isLoading: isUploadingProfile }] = useUploadTeamProfileFileMutation()
  const [getUserByEmail] = useLazyGetUserByEmailQuery()
  const [fetchUsersBatch] = useFetchUsersBatchMutation()
  
  // Check permissions
  const canRegisterTeam = currentUser && hasPermission(currentUser.role, 'REGISTER_TEAM') // Student đăng ký team
  const canManageTeam = currentUser && hasPermission(currentUser.role, 'MANAGE_TEAM')
  const canAssignCases = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')
  const canViewAllTeams = canAssignCases // Secretary/Support can view all teams
  const isSecretary = currentUser?.role === 'secretary' // Only secretary can change manager

  // Get current user's team
  const userTeam = teams.find(t => 
    t.members.includes(currentUser?.id || '') || 
    t.coach_id === currentUser?.id
  )
  
  // Get users for display - only for teams that current user can view
  const displayTeams = (canViewAllTeams ? teams : userTeam ? [userTeam] : []).filter(
    (t) => t.status !== 'deleted'
  )
  
  // Collect all unique user IDs from displayTeams (members + coaches)
  const allUserIds = displayTeams.flatMap(team => [
    ...team.members,
    ...(team.coach_id ? [team.coach_id] : [])
  ]).filter((id, index, arr) => arr.indexOf(id) === index && id) // Remove duplicates and empty
  
  const { data: users = [] } = useGetUsersBatchQuery(allUserIds, { skip: allUserIds.length === 0 || isLoading })

  const [openDialog, setOpenDialog] = useState(false)
  const [viewDialog, setViewDialog] = useState(false)
  const [editingTeam, setEditingTeam] = useState<Team | null>(null)
  const [viewingTeam, setViewingTeam] = useState<Team | null>(null)
  const [formData, setFormData] = useState<TeamCreate>({
    name: '',
    university: '',
    members: [],
    coach_id: '',
    profile_text: '',
  })
  const [memberEmails, setMemberEmails] = useState<MemberEmail[]>([])
  const [coachEmail, setCoachEmail] = useState<string>('')
  const [memberCards, setMemberCards] = useState<MemberCardsMap>({})
  const [coachInfo, setCoachInfo] = useState<{ id?: string; name?: string; isValid?: boolean; isLoading?: boolean; error?: string }>({})
  // UAT-TC-1 #2: thông tin đăng ký cuộc thi của từng member (MSSV, DOB, khoa, bio…)
  const [memberDetailsByIdx, setMemberDetailsByIdx] = useState<Record<number, MemberDetails>>({})
  const [coachDetails, setCoachDetails] = useState<CoachDetails>({ ...EMPTY_COACH_DETAILS })
  // UAT-TC #4 Hồ sơ đội: file PDF Team Profile
  const [profileFile, setProfileFile] = useState<MemberCardInfo | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  // Snackbar toàn cục (không bị Dialog overlay che) — cho mọi thông báo create/update team
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({ open: false, message: '', severity: 'info' })

  useEffect(() => {
    if (!canRegisterTeam && !canViewAllTeams && !userTeam) {
      setErrorMessage('Bạn không có quyền xem danh sách teams. Chỉ có thể xem team của mình.')
    }
  }, [canRegisterTeam, canViewAllTeams, userTeam])

  // Helper function để load team data vào form
  const loadTeamData = async (team: Team) => {
    setEditingTeam(team)
    setFormData({
      name: team.name,
      university: team.university,
      members: team.members,
      coach_id: team.coach_id || '',
      profile_text: team.profile_text || '',
      member_cards: team.member_cards || {},
      member_details: team.member_details || {},
      coach_details: team.coach_details || null,
    })
    setMemberCards(team.member_cards || {})
    // Load member_details: map từ user_id sang index dựa vào thứ tự team.members
    const detailsByIdx: Record<number, MemberDetails> = {}
    team.members.forEach((uid, idx) => {
      detailsByIdx[idx] = {
        ...EMPTY_MEMBER_DETAILS,
        ...(team.member_details?.[uid] || {}),
      }
    })
    setMemberDetailsByIdx(detailsByIdx)
    setCoachDetails({ ...EMPTY_COACH_DETAILS, ...(team.coach_details || {}) })
    setProfileFile(team.profile_file || null)
    
    // Fetch user info for team members (use batch API)
    try {
      const teamMemberIds = team.members.filter(id => id)
      const coachId = team.coach_id ? [team.coach_id] : []
      const allIds = [...teamMemberIds, ...coachId]
      
      if (allIds.length > 0) {
        // Use RTK Query mutation to fetch users
        const fetchedUsers = await fetchUsersBatch(allIds).unwrap()
        
        // Populate memberEmails from fetched users
        const memberEmailsData: MemberEmail[] = team.members.map(memberId => {
          const userInfo = fetchedUsers.find((u: any) => u.id === memberId)
          if (userInfo) {
            return {
              email: userInfo.email,
              userId: userInfo.id,
              fullName: userInfo.full_name || userInfo.username,
              role: userInfo.role,
              isValid: true,
            }
          } else {
            return {
              email: '',
              userId: memberId,
              isValid: false,
            }
          }
        })
        setMemberEmails(memberEmailsData)
        
        // Populate coach info if exists
        if (team.coach_id) {
          const coachUser = fetchedUsers.find((u: any) => u.id === team.coach_id)
          if (coachUser) {
            setCoachEmail(coachUser.email)
            setCoachInfo({
              id: coachUser.id,
              name: coachUser.full_name || coachUser.username,
              isValid: true,
            })
          } else {
            setCoachEmail('')
            setCoachInfo({})
          }
        } else {
          setCoachEmail('')
          setCoachInfo({})
        }
      } else {
        setMemberEmails([])
        setCoachEmail('')
        setCoachInfo({})
      }
    } catch (err) {
      console.error('Error fetching user info for team members:', err)
      // Fallback: use users from batch query if available
      const memberEmailsData: MemberEmail[] = team.members.map(memberId => {
        const userInfo = users.find(u => u.id === memberId)
        return userInfo ? {
          email: userInfo.email,
          userId: userInfo.id,
          fullName: userInfo.full_name || userInfo.username,
          role: userInfo.role,
          isValid: true,
        } : {
          email: '',
          userId: memberId,
          isValid: false,
        }
      })
      setMemberEmails(memberEmailsData)
      
      if (team.coach_id) {
        const coachUser = users.find(u => u.id === team.coach_id)
        if (coachUser) {
          setCoachEmail(coachUser.email)
          setCoachInfo({
            id: coachUser.id,
            name: coachUser.full_name || coachUser.username,
            isValid: true,
          })
        } else {
          setCoachEmail('')
          setCoachInfo({})
        }
      } else {
        setCoachEmail('')
        setCoachInfo({})
      }
    }
  }

  // Create team function - matches moduleFunctions.ts
  const handleCreate = () => {
    setEditingTeam(null)
    setFormData({
      name: '',
      university: '',
      members: currentUser?.role === 'student' ? [currentUser.id] : [], // Student tự động là member đầu tiên
      coach_id: '',
      profile_text: '',
      member_cards: {},
    })
    // Initialize với current user email (disabled)
    if (currentUser?.role === 'student' && currentUser.email) {
      setMemberEmails([{
        email: currentUser.email,
        userId: currentUser.id,
        fullName: currentUser.full_name || currentUser.username,
        role: currentUser.role,
        isValid: true,
      }])
      // Pre-fill chi tiết thí sinh đầu tiên = current user
      setMemberDetailsByIdx({
        0: {
          ...EMPTY_MEMBER_DETAILS,
          full_name: currentUser.full_name || currentUser.username || '',
          email: currentUser.email,
        },
      })
    } else {
      setMemberEmails([])
      setMemberDetailsByIdx({})
    }
    setCoachEmail('')
    setMemberCards({})
    setCoachInfo({})
    setCoachDetails({ ...EMPTY_COACH_DETAILS })
    setProfileFile(null)
    setOpenDialog(true)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  // Update team function - matches moduleFunctions.ts
  const handleUpdate = async (team: Team) => {
    await loadTeamData(team)
    setOpenDialog(true)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingTeam(null)
    setFormData({
      name: '',
      university: '',
      members: [],
      coach_id: '',
      profile_text: '',
      member_cards: {},
    })
    setMemberEmails([])
    setCoachEmail('')
    setMemberCards({})
    setCoachInfo({})
    setMemberDetailsByIdx({})
    setCoachDetails({ ...EMPTY_COACH_DETAILS })
    setProfileFile(null)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  // View team function - matches moduleFunctions.ts
  const handleView = (team: Team) => {
    setViewingTeam(team)
    setViewDialog(true)
  }

  const handleCloseViewDialog = () => {
    setViewDialog(false)
    setViewingTeam(null)
  }

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  // Handle member email change and lookup
  const handleMemberEmailChange = async (index: number, email: string) => {
    // Prevent changing manager (first member) if not secretary
    if (index === 0 && editingTeam && !isSecretary) {
      return
    }

    const newMemberEmails = [...memberEmails]
    newMemberEmails[index] = {
      email,
      isLoading: true,
      isValid: false,
    }
    setMemberEmails(newMemberEmails)

    // Validate email format
    if (!isValidEmail(email)) {
      newMemberEmails[index] = {
        email,
        isValid: false,
        isLoading: false,
        error: 'Email không hợp lệ',
      }
      setMemberEmails(newMemberEmails)
      return
    }

    try {
      const result = await getUserByEmail(email).unwrap()
      if (result.role === 'student') {
        const oldUserId = memberEmails[index]?.userId
        newMemberEmails[index] = {
          email,
          userId: result.id,
          fullName: result.full_name || result.username,
          role: result.role,
          isValid: true,
          isLoading: false,
        }
        // Update formData members
        const newMembers = [...formData.members]
        if (index === 0 && !editingTeam && currentUser?.role === 'student') {
          // First member is always current user when creating
          newMembers[0] = currentUser.id
        } else {
          newMembers[index] = result.id
        }
        setFormData({ ...formData, members: newMembers })
        if (oldUserId && oldUserId !== result.id) {
          setMemberCards((prev) => {
            const next = { ...prev }
            delete next[oldUserId]
            return next
          })
        }
      } else {
        newMemberEmails[index] = {
          email,
          isValid: false,
          isLoading: false,
          error: 'Chỉ có thể thêm sinh viên vào đội thi',
        }
      }
    } catch (err: any) {
      newMemberEmails[index] = {
        email,
        isValid: false,
        isLoading: false,
        error: err?.data?.detail || 'Không tìm thấy người dùng với email này',
      }
    }
    setMemberEmails(newMemberEmails)
  }

  // Handle coach email change and lookup
  const handleCoachEmailChange = async (email: string) => {
    setCoachEmail(email)
    setCoachInfo({ isLoading: true, isValid: false })

    if (!email) {
      setCoachInfo({})
      setFormData({ ...formData, coach_id: '' })
      return
    }

    // Validate email format
    if (!isValidEmail(email)) {
      setCoachInfo({ isValid: false, error: 'Email không hợp lệ' })
      return
    }

    try {
      const result = await getUserByEmail(email).unwrap()
      if (result.role === 'coach') {
        setCoachInfo({
          id: result.id,
          name: result.full_name || result.username,
          isValid: true,
        })
        setFormData({ ...formData, coach_id: result.id })
      } else {
        setCoachInfo({
          isValid: false,
          error: 'Chỉ có thể chọn huấn luyện viên',
        })
      }
    } catch (err: any) {
      setCoachInfo({
        isValid: false,
        error: err?.data?.detail || 'Không tìm thấy huấn luyện viên với email này',
      })
    }
  }

  const handleAddMember = () => {
    const newIdx = memberEmails.length
    setMemberEmails([...memberEmails, { email: '', isValid: false }])
    setFormData({ ...formData, members: [...formData.members, ''] })
    setMemberDetailsByIdx((prev) => ({ ...prev, [newIdx]: { ...EMPTY_MEMBER_DETAILS } }))
  }

  const handleRemoveMember = (index: number) => {
    // Cannot remove first member (current user) when creating new team
    if (index === 0 && !editingTeam && currentUser?.role === 'student') {
      return
    }
    // Cannot remove manager (first member) when editing unless secretary
    if (index === 0 && editingTeam && !isSecretary) {
      return
    }
    const newMemberEmails = memberEmails.filter((_, i) => i !== index)
    const newMembers = formData.members.filter((_, i) => i !== index)
    const removedUserId = memberEmails[index]?.userId
    setMemberEmails(newMemberEmails)
    setFormData({ ...formData, members: newMembers })
    if (removedUserId) {
      setMemberCards((prev) => {
        const next = { ...prev }
        delete next[removedUserId]
        return next
      })
    }
    // Re-index memberDetailsByIdx (xoá index bị remove, dồn các index lớn hơn lên)
    setMemberDetailsByIdx((prev) => {
      const next: Record<number, MemberDetails> = {}
      Object.entries(prev).forEach(([k, v]) => {
        const i = Number(k)
        if (i < index) next[i] = v
        else if (i > index) next[i - 1] = v
      })
      return next
    })
  }

  const updateMemberDetail = (idx: number, field: keyof MemberDetails, value: string) => {
    setMemberDetailsByIdx((prev) => ({
      ...prev,
      [idx]: { ...(prev[idx] || EMPTY_MEMBER_DETAILS), [field]: value },
    }))
  }

  const handleMemberCardUpload = async (member: MemberEmail, file?: File) => {
    if (!file) return
    if (!member.userId) {
      setErrorMessage('Vui lòng xác thực email thành viên trước khi upload thẻ SV.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('Thẻ SV vượt quá 5MB.')
      return
    }
    const lower = file.name.toLowerCase()
    const isPdfOrJpg = lower.endsWith('.pdf') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')
    if (!isPdfOrJpg) {
      setErrorMessage('Thẻ SV chỉ chấp nhận PDF/JPG.')
      return
    }
    try {
      const uploaded = await uploadMemberCard({ memberId: member.userId, file }).unwrap()
      setMemberCards((prev) => ({ ...prev, [member.userId as string]: uploaded }))
      setSuccessMessage(`Đã tải thẻ SV cho ${member.fullName || member.email}`)
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Upload thẻ SV thất bại')
    }
  }

  const handleProfileFileUpload = async (file?: File) => {
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      notify('Hồ sơ đội thi vượt quá 10MB.', 'error')
      return
    }
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      notify('Hồ sơ đội thi chỉ chấp nhận PDF.', 'error')
      return
    }
    try {
      const uploaded = await uploadTeamProfileFile({ file }).unwrap()
      setProfileFile(uploaded)
      notify(`Đã tải hồ sơ đội thi: ${file.name}`, 'success')
    } catch (err: any) {
      notify(err?.data?.detail || err?.message || 'Upload hồ sơ đội thi thất bại', 'error')
    }
  }

  // Helper: hiển thị thông báo qua cả Snackbar (global, không bị Dialog che)
  // và Alert trong dialog (để user thấy ngay tại form khi submit fail).
  const notify = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info'
  ) => {
    setSnackbar({ open: true, message, severity })
    if (severity === 'error' || severity === 'warning') {
      setErrorMessage(message)
    } else if (severity === 'success') {
      setSuccessMessage(message)
    }
  }

  const handleSubmit = async () => {
    setErrorMessage(null)
    setSuccessMessage(null)

    // Validation
    if (!formData.name.trim()) {
      notify('Tên đội là bắt buộc', 'error')
      return
    }
    if (!formData.university.trim()) {
      notify('Trường đại học là bắt buộc', 'error')
      return
    }

    // Build final members list from memberEmails
    const validMembers: string[] = []
    for (let i = 0; i < memberEmails.length; i++) {
      const memberEmail = memberEmails[i]
      if (memberEmail.userId) {
        validMembers.push(memberEmail.userId)
      } else if (i === 0 && currentUser?.role === 'student' && currentUser.id) {
        // First member is always current user
        validMembers.push(currentUser.id)
      } else if (memberEmail.email) {
        notify(
          `Vui lòng kiểm tra lại email của thành viên ${i + 1}: ${memberEmail.email}`,
          'error'
        )
        return
      }
    }

    if (validMembers.length === 0) {
      notify('Phải có ít nhất 1 thành viên', 'error')
      return
    }
    if (validMembers.length < 5 || validMembers.length > 8) {
      notify(
        `Theo quy định cuộc thi: đội phải có từ 5 đến 8 thành viên (không tính HLV). Hiện tại: ${validMembers.length}.`,
        'error'
      )
      return
    }
    if (new Set(validMembers).size !== validMembers.length) {
      notify('Không được trùng lặp thành viên.', 'error')
      return
    }
    const cardsForMembers: MemberCardsMap = {}
    for (const memberId of validMembers) {
      const card = memberCards[memberId]
      if (!card) {
        notify('Cần upload đủ thẻ SV cho toàn bộ thành viên trước khi gửi.', 'error')
        return
      }
      cardsForMembers[memberId] = card
    }

    // UAT-TC #4 Hồ sơ đội: bắt buộc tải PDF Team Profile
    if (!profileFile) {
      notify('Cần tải PDF Hồ sơ đội thi (Team Profile) trước khi hoàn tất đăng ký.', 'error')
      return
    }

    // UAT-TC-1 #2: Validate chi tiết đăng ký từng thành viên
    const detailsForMembers: Record<string, MemberDetails> = {}
    for (let i = 0; i < validMembers.length; i++) {
      const userId = validMembers[i]
      const d = memberDetailsByIdx[i]
      if (!d) {
        notify(`Vui lòng nhập thông tin đăng ký cho Thành viên ${i + 1}.`, 'error')
        return
      }
      const missing: string[] = []
      if (!d.student_id?.trim()) missing.push('MSSV')
      if (!d.full_name?.trim()) missing.push('Họ tên')
      if (!d.date_of_birth?.trim()) missing.push('Ngày sinh')
      if (!d.university?.trim()) missing.push('Trường')
      if (!d.faculty?.trim()) missing.push('Khoa')
      if (!d.email?.trim()) missing.push('Email')
      if (missing.length) {
        notify(
          `Thành viên ${i + 1} thiếu trường bắt buộc: ${missing.join(', ')}.`,
          'error'
        )
        return
      }
      detailsForMembers[userId] = {
        student_id: d.student_id.trim(),
        full_name: d.full_name.trim(),
        date_of_birth: d.date_of_birth.trim(),
        university: d.university.trim(),
        faculty: d.faculty.trim(),
        email: d.email.trim(),
        bio: (d.bio || '').trim(),
      }
    }

    // Validate coach details (chỉ khi có coach hoặc người dùng đã nhập bất kỳ trường nào)
    let coachPayload: CoachDetails | null = null
    const hasAnyCoachField =
      coachEmail.trim() ||
      coachDetails.full_name.trim() ||
      coachDetails.university.trim() ||
      coachDetails.faculty.trim() ||
      coachDetails.bio.trim()
    if (hasAnyCoachField) {
      const missing: string[] = []
      if (!coachDetails.full_name.trim()) missing.push('Họ tên HLV')
      if (!(coachEmail.trim() || coachDetails.email.trim())) missing.push('Email HLV')
      if (!coachDetails.university.trim()) missing.push('Trường')
      if (!coachDetails.faculty.trim()) missing.push('Khoa/chuyên môn')
      if (missing.length) {
        notify(`Thông tin HLV thiếu trường: ${missing.join(', ')}.`, 'error')
        return
      }
      coachPayload = {
        full_name: coachDetails.full_name.trim(),
        email: (coachEmail.trim() || coachDetails.email.trim()),
        university: coachDetails.university.trim(),
        faculty: coachDetails.faculty.trim(),
        bio: (coachDetails.bio || '').trim(),
      }
    }

    // Prepare final form data
    const finalFormData: TeamCreate = {
      name: formData.name.trim(),
      university: formData.university.trim(),
      members: validMembers,
      coach_id: coachInfo.id || undefined,
      profile_text: (formData.profile_text || '').trim() || undefined,
      member_cards: cardsForMembers,
      member_details: detailsForMembers,
      coach_details: coachPayload,
      profile_file: profileFile,
    }

    try {
      if (editingTeam) {
        await updateTeam({ id: editingTeam.id, ...finalFormData }).unwrap()
        notify('Cập nhật đội thi thành công!', 'success')
      } else {
        await createTeam(finalFormData).unwrap()
        notify('Đăng ký đội thi thành công!', 'success')
      }
      handleCloseDialog()
      refetch()
    } catch (err: any) {
      console.error('[Team Registration Error]', err)
      const detail =
        err?.data?.detail ||
        err?.error ||
        err?.message ||
        'Có lỗi xảy ra khi đăng ký đội thi. Vui lòng kiểm tra lại thông tin.'
      // KHÔNG đóng dialog — giữ form để user sửa lại
      notify(`Tạo đội thất bại: ${detail}`, 'error')
    }
  }


  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Quản lý Đội thi"
        subtitle="Đăng ký đội, thành viên và trạng thái duyệt theo từng cuộc thi."
        icon={<Groups />}
        actions={
          canRegisterTeam ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{
                background: gradientBrand,
                '&:hover': {
                  background: gradientBrand,
                  filter: 'brightness(1.06)',
                },
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
              }}
            >
              Đăng ký đội mới
            </Button>
          ) : null
        }
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={(theme) => getTablePaperSx(theme)}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Tên đội</strong></TableCell>
                <TableCell><strong>Trường đại học</strong></TableCell>
                <TableCell><strong>Số thành viên</strong></TableCell>
                <TableCell><strong>Huấn luyện viên</strong></TableCell>
                <TableCell><strong>Trạng thái</strong></TableCell>
                <TableCell><strong>Số cuộc thi</strong></TableCell>
                <TableCell align="right"><strong>Thao tác</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayTeams.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {canRegisterTeam ? 'Chưa có đội thi nào. Hãy đăng ký đội mới!' : 'Bạn chưa tham gia đội thi nào.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayTeams.map((team) => {
                  const coach = team.coach_id ? users.find(u => u.id === team.coach_id) : null
                  return (
                    <TableRow key={team.id} hover>
                      <TableCell>{team.name}</TableCell>
                      <TableCell>{team.university}</TableCell>
                      <TableCell>{team.members.length}</TableCell>
                      <TableCell>{coach ? (coach.full_name || coach.username) : '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_OPTIONS.find(s => s.value === team.status)?.label || team.status}
                        color={STATUS_COLORS[team.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{team.assigned_cases?.length || 0}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleView(team)}
                        color="primary"
                      >
                        <Visibility />
                      </IconButton>
                      {((canManageTeam && userTeam?.id === team.id) || canAssignCases) && (
                        <IconButton
                          size="small"
                          onClick={() => handleUpdate(team)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600 }}>
          {editingTeam ? 'Chỉnh sửa đội thi' : 'Đăng ký đội thi mới'}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {/* Inline error trong dialog — hiển thị ngay tại form khi submit fail */}
          {errorMessage && (
            <Alert
              severity="error"
              onClose={() => setErrorMessage(null)}
              sx={{ mt: 1, mb: 1 }}
            >
              {errorMessage}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: { xs: 1, sm: 2 } }}>
            <TextField
              label="Tên đội"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Trường đại học"
              value={formData.university}
              onChange={(e) => setFormData({ ...formData, university: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Mô tả hồ sơ đội (Team Profile)"
              value={formData.profile_text || ''}
              onChange={(e) => setFormData({ ...formData, profile_text: e.target.value })}
              fullWidth
              multiline
              minRows={3}
              helperText="Giới thiệu ngắn về thành tích, định hướng, kỹ năng của đội."
            />

            {/* UAT-TC #4 Hồ sơ đội: upload PDF Team Profile */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Hồ sơ đội thi (PDF) <Typography component="span" color="error.main" fontSize="0.85rem">*</Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tệp PDF tổng hợp giới thiệu đội — bắt buộc để hoàn tất đăng ký. Tối đa 10MB.
                  </Typography>
                </Box>
                <Button
                  component="label"
                  variant={profileFile ? 'contained' : 'outlined'}
                  startIcon={<CloudUpload />}
                  size="small"
                  disabled={isUploadingProfile}
                  sx={{ textTransform: 'none' }}
                >
                  {isUploadingProfile ? 'Đang tải...' : profileFile ? 'Thay file' : 'Tải lên PDF'}
                  <input
                    hidden
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleProfileFileUpload(e.target.files?.[0])}
                  />
                </Button>
              </Box>
              {profileFile && (
                <Alert severity="success" sx={{ mt: 1.5 }} icon={<CheckCircle />}>
                  Đã tải: <strong>{profileFile.file_name}</strong>
                </Alert>
              )}
            </Paper>

            <Box>
              <Alert severity="info" sx={{ mb: 1 }}>
                Quy định: 5–8 thành viên (không tính HLV), không trùng email; mỗi người chỉ thuộc một đội. BTC duyệt hồ
                sơ trước khi đội được xem tài liệu đề vụ được phân công.
              </Alert>
              <Alert severity="warning" sx={{ mb: 1 }}>
                Bắt buộc upload đủ thẻ SV PDF/JPG (tối đa 5MB) cho tất cả thành viên trước khi gửi hồ sơ.
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Thành viên
                </Typography>
                <Button 
                  size="small" 
                  onClick={handleAddMember}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  Thêm thành viên
                </Button>
              </Box>
              {memberEmails.map((memberEmail, index) => {
                const details = memberDetailsByIdx[index] || EMPTY_MEMBER_DETAILS
                const memberLabel = `Thành viên ${index + 1}${index === 0 && !editingTeam && currentUser?.role === 'student' ? ' (Bạn / Đội trưởng)' : ''}${index === 0 && editingTeam ? ' (Quản lý đội)' : ''}`
                return (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{ p: 2, mb: 2, borderRadius: 2 }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {memberLabel}
                    </Typography>
                    {memberEmails.length > 1 && !(index === 0 && !editingTeam && currentUser?.role === 'student') && !(index === 0 && editingTeam && !isSecretary) && (
                      <IconButton onClick={() => handleRemoveMember(index)} color="error" size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  {/* Email + Lookup user account */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box sx={{ flex: 1, width: '100%' }}>
                      <TextField
                        label="Email (đăng nhập hệ thống)"
                        type="email"
                        value={memberEmail.email}
                        onChange={(e) => {
                          const newEmails = [...memberEmails]
                          newEmails[index] = { ...memberEmails[index], email: e.target.value }
                          setMemberEmails(newEmails)
                          // Đồng bộ email vào member_details
                          updateMemberDetail(index, 'email', e.target.value)
                        }}
                        onBlur={(e) => {
                          if (e.target.value && e.target.value !== memberEmail.email) {
                            handleMemberEmailChange(index, e.target.value)
                          }
                        }}
                        disabled={
                          !!(index === 0 && !editingTeam && currentUser?.role === 'student') ||
                          !!(index === 0 && editingTeam && !isSecretary)
                        }
                        required
                        fullWidth
                        error={memberEmail.error !== undefined}
                        helperText={
                          memberEmail.isLoading
                            ? 'Đang tìm kiếm...'
                            : index === 0 && editingTeam && !isSecretary
                              ? 'Chỉ thư ký mới có thể thay đổi người quản lý đội thi'
                              : memberEmail.error || (memberEmail.isValid && memberEmail.fullName ? `${memberEmail.fullName} (${memberEmail.role})` : 'Email phải đã có account trên hệ thống')
                        }
                        InputProps={{
                          endAdornment: memberEmail.isLoading ? (
                            <CircularProgress size={20} />
                          ) : memberEmail.isValid ? (
                            <CheckCircle color="success" fontSize="small" />
                          ) : memberEmail.error ? (
                            <ErrorIcon color="error" fontSize="small" />
                          ) : null,
                        }}
                      />
                    </Box>
                    <Box sx={{ minWidth: { xs: '100%', sm: 180 } }}>
                      <Button
                        component="label"
                        variant={memberEmail.userId && memberCards[memberEmail.userId] ? 'contained' : 'outlined'}
                        startIcon={<CloudUpload />}
                        size="small"
                        disabled={!memberEmail.userId || isUploadingCard}
                        fullWidth
                        sx={{ textTransform: 'none' }}
                      >
                        {memberEmail.userId && memberCards[memberEmail.userId] ? 'Đã tải thẻ SV' : 'Tải thẻ SV (PDF/JPG)'}
                        <input
                          hidden
                          type="file"
                          accept=".pdf,.jpg,.jpeg"
                          onChange={(e) => handleMemberCardUpload(memberEmail, e.target.files?.[0])}
                        />
                      </Button>
                    </Box>
                  </Box>

                  {/* UAT-TC-1 #2: Thông tin đăng ký cuộc thi của member */}
                  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                    <TextField
                      label="MSSV"
                      value={details.student_id}
                      onChange={(e) => updateMemberDetail(index, 'student_id', e.target.value)}
                      required
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Họ và tên"
                      value={details.full_name}
                      onChange={(e) => updateMemberDetail(index, 'full_name', e.target.value)}
                      required
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Ngày sinh"
                      type="date"
                      value={details.date_of_birth}
                      onChange={(e) => updateMemberDetail(index, 'date_of_birth', e.target.value)}
                      required
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Trường"
                      value={details.university}
                      onChange={(e) => updateMemberDetail(index, 'university', e.target.value)}
                      required
                      fullWidth
                      size="small"
                      placeholder="VD: UEL, ULAW, HCMULAW…"
                    />
                    <TextField
                      label="Khoa / Ngành"
                      value={details.faculty}
                      onChange={(e) => updateMemberDetail(index, 'faculty', e.target.value)}
                      required
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Giới thiệu ngắn về bản thân"
                      value={details.bio}
                      onChange={(e) => updateMemberDetail(index, 'bio', e.target.value)}
                      fullWidth
                      size="small"
                      multiline
                      minRows={1}
                      maxRows={3}
                      placeholder="Kinh nghiệm, định hướng, kỹ năng…"
                      sx={{ gridColumn: { sm: '1 / -1' } }}
                    />
                  </Box>
                </Paper>
              )})}
            </Box>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Huấn luyện viên (tùy chọn — bỏ trống nếu không có)
              </Typography>
              <TextField
                label="Email huấn luyện viên (đăng nhập hệ thống)"
                type="email"
                value={coachEmail}
                onChange={(e) => {
                  setCoachEmail(e.target.value)
                  setCoachDetails((prev) => ({ ...prev, email: e.target.value }))
                }}
                onBlur={(e) => {
                  if (e.target.value !== coachEmail) {
                    handleCoachEmailChange(e.target.value)
                  }
                }}
                fullWidth
                size="small"
                sx={{ mb: 1.5 }}
                error={coachInfo.error !== undefined}
                helperText={
                  coachInfo.isLoading
                    ? 'Đang tìm kiếm...'
                    : coachInfo.error || (coachInfo.isValid && coachInfo.name ? coachInfo.name : 'Email phải đã có account hoặc để trống nếu không có HLV')
                }
                InputProps={{
                  endAdornment: coachInfo.isLoading ? (
                    <CircularProgress size={20} />
                  ) : coachInfo.isValid ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : coachInfo.error ? (
                    <ErrorIcon color="error" fontSize="small" />
                  ) : null,
                }}
              />
              {/* Thông tin đăng ký HLV — bắt buộc nếu nhập bất kỳ trường nào */}
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                <TextField
                  label="Họ và tên HLV"
                  value={coachDetails.full_name}
                  onChange={(e) => setCoachDetails({ ...coachDetails, full_name: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Trường giảng dạy"
                  value={coachDetails.university}
                  onChange={(e) => setCoachDetails({ ...coachDetails, university: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Khoa / Chuyên môn"
                  value={coachDetails.faculty}
                  onChange={(e) => setCoachDetails({ ...coachDetails, faculty: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Lý lịch / Kinh nghiệm ngắn"
                  value={coachDetails.bio}
                  onChange={(e) => setCoachDetails({ ...coachDetails, bio: e.target.value })}
                  fullWidth
                  size="small"
                  multiline
                  minRows={1}
                  maxRows={3}
                  sx={{ gridColumn: { sm: '1 / -1' } }}
                />
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isCreating || isUpdating || isUploadingCard || isUploadingProfile}
            sx={{
              background: gradientBrand,
              '&:hover': {
                background: gradientBrand,
                filter: 'brightness(1.06)',
              },
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
            }}
          >
            {isCreating || isUpdating
              ? <CircularProgress size={20} />
              : (editingTeam ? 'Cập nhật' : 'Hoàn tất đăng ký')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Team Dialog */}
      {viewingTeam && (
        <TeamDetailDialog
          open={viewDialog}
          onClose={handleCloseViewDialog}
          team={viewingTeam}
          canReview={!!canAssignCases}
          onReviewed={() => refetch()}
        />
      )}

      {/* Snackbar global — hiển thị thông báo create/update team kể cả khi dialog đang mở */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 8000 : 4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%', minWidth: 360 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

// Team Detail Dialog Component
function TeamDetailDialog({ 
  open, 
  onClose, 
  team,
  canReview,
  onReviewed,
}: { 
  open: boolean
  onClose: () => void
  team: Team
  canReview?: boolean
  onReviewed?: () => void
}) {
  const [reviewTeam, { isLoading: reviewing }] = useReviewTeamMutation()
  const [rejectReason, setRejectReason] = useState('')
  const [reviewError, setReviewError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setRejectReason('')
      setReviewError(null)
    }
  }, [open, team.id])

  const { data: teamCases = [] } = useGetTeamCasesQuery(team.id, { skip: !open })
  
  // Get users for team members and coach
  const memberIds = team.members
  const coachId = team.coach_id ? [team.coach_id] : []
  const allUserIds = [...memberIds, ...coachId]
  const { data: users = [] } = useGetUsersBatchQuery(allUserIds, { skip: !open || allUserIds.length === 0 })
  
  const teamMembers = team.members.map(id => users.find(u => u.id === id)).filter(Boolean)
  const coach = team.coach_id ? users.find(u => u.id === team.coach_id) : null

  const handleApprove = async () => {
    setReviewError(null)
    try {
      await reviewTeam({ teamId: team.id, decision: 'approve' }).unwrap()
      onReviewed?.()
      onClose()
    } catch (e: any) {
      setReviewError(e?.data?.detail || e?.message || 'Duyệt thất bại')
    }
  }

  const handleReject = async () => {
    setReviewError(null)
    const r = rejectReason.trim()
    if (!r) {
      setReviewError('Vui lòng nhập lý do từ chối.')
      return
    }
    try {
      await reviewTeam({ teamId: team.id, decision: 'reject', reason: r }).unwrap()
      onReviewed?.()
      onClose()
    } catch (e: any) {
      setReviewError(e?.data?.detail || e?.message || 'Từ chối thất bại')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Groups />
          <Typography variant="h6">{team.name}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {reviewError && (
            <Alert severity="error" onClose={() => setReviewError(null)}>
              {reviewError}
            </Alert>
          )}
          {team.status === 'rejected' && team.rejection_reason && (
            <Alert severity="warning">
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Lý do từ chối gần nhất
              </Typography>
              <Typography variant="body2">{team.rejection_reason}</Typography>
            </Alert>
          )}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Trường đại học
            </Typography>
            <Typography variant="body1">{team.university}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Thành viên ({team.members.length} người)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {teamMembers.map((member, index) => (
                <Chip
                  key={member?.id || index}
                  label={`${member?.full_name || member?.username} (${member?.email})`}
                  size="small"
                />
              ))}
            </Box>
          </Box>

          {coach && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Huấn luyện viên
              </Typography>
              <Typography variant="body1">
                {coach.full_name || coach.username} ({coach.email})
              </Typography>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Trạng thái
            </Typography>
            <Chip
              label={STATUS_OPTIONS.find(s => s.value === team.status)?.label || team.status}
              color={STATUS_COLORS[team.status] || 'default'}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Cuộc thi được phân bổ ({teamCases.length})
            </Typography>
            {teamCases.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Chưa có cuộc thi nào được phân bổ
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {teamCases.map((caseItem: any) => (
                  <Paper key={caseItem.id} sx={{ p: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {caseItem.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Trạng thái: {caseItem.status} | Deadline: {caseItem.deadline ? new Date(caseItem.deadline).toLocaleDateString('vi-VN') : 'Chưa có'}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>

          {canReview && team.status === 'registered' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Duyệt hồ sơ đội (BTC)
              </Typography>
              <TextField
                label="Lý do từ chối (bắt buộc nếu từ chối)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="success"
                  disabled={reviewing}
                  onClick={handleApprove}
                  sx={{ textTransform: 'none' }}
                >
                  Duyệt
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={reviewing}
                  onClick={handleReject}
                  sx={{ textTransform: 'none' }}
                >
                  Từ chối
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1 }}
        >
          Đóng
        </Button>
      </DialogActions>
    </Dialog>
  )
}
